// US-11 — /billing rediseñada (UC-11.06)
// US-25 UC-25.01 — TimeRangeSelector: granularidad + periodo en URL.
// URL state preserved (filters via TanStack Router useSearch).
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FilePlus2, RefreshCw, Star } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { useInvoices } from "@/hooks/useInvoices";
import { useInvoiceDetail } from "@/hooks/useInvoiceDetail";
import { useHydrateBilling } from "@/hooks/useHydrateBilling";
import { BillingTable } from "@/components/billing/BillingTable";
import {
  BillingFilterBar,
  type FilterValues,
} from "@/components/billing/BillingFilterBar";
import { TimeRangeSelector } from "@/components/billing/TimeRangeSelector";
import { BillingEvolutionChart } from "@/components/billing/BillingEvolutionChart";
import { FiscalPositionCard } from "@/components/billing/FiscalPositionCard";
import { QuarterMonthsBreakdown } from "@/components/billing/QuarterMonthsBreakdown";
import { PortfolioConcentration } from "@/components/billing/PortfolioConcentration";
import { YearEndProjectionCard } from "@/components/billing/YearEndProjectionCard";
import { InvoiceDetailPanel } from "@/components/billing/InvoiceDetailPanel";
import { NewInvoicePanel } from "@/components/billing/NewInvoicePanel";
import { Card, CardHeader, CardTitle, KPIStat } from "@/components/ui/v2";
import { Amount } from "@/components/masked/Amount";
import { useSavedViews } from "@/hooks/useSavedViews";
import { currentPeriod, periodLabel, periodRange } from "@/lib/period";
import type { Period } from "@/domain/billing/fiscalPosition";
import type { Invoice, InvoiceState } from "@/types/billing";

const STATE_VALUES = [
  "emitida",
  "pagada",
  "borrador",
  "anulada",
  "rectificativa",
] as const;

const billingSearchSchema = z.object({
  view: z.enum(["q303", "q130"]).optional(),
  status: z.array(z.enum(STATE_VALUES)).optional(),
  customer: z.string().optional(),
  // `billing_invoice_index.id` is BIGSERIAL — coerced to number when present.
  selectedId: z.coerce.number().int().positive().optional(),
  // US-25 UC-25.01 — Periodo serializado como campos separados en la URL.
  // Todos opcionales para no romper links anteriores; el fallback es
  // currentPeriod("quarter") (mismo comportamiento que el estado pre-US-25).
  granularity: z.enum(["year", "quarter", "month"]).optional(),
  periodYear: z.coerce.number().int().optional(),
  periodQuarter: z.coerce.number().int().min(1).max(4).optional(),
  periodMonth: z.coerce.number().int().min(1).max(12).optional(),
});

type BillingSearch = z.infer<typeof billingSearchSchema>;

function BillingPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/billing" });
  const { add: addSavedView } = useSavedViews();

  const filters: FilterValues = useMemo(
    () => ({
      status: search.status as InvoiceState[] | undefined,
      customer: search.customer,
    }),
    [search.status, search.customer],
  );

  // US-25 UC-25.01 — Derivar Period desde la URL; fallback = trimestre actual.
  // Los campos son opcionales (para no romper links viejos) y se reconstruyen
  // en función de `granularity`. Si faltan datos para la granularidad indicada
  // se usa currentPeriod() de esa granularidad como fallback seguro.
  const period: Period = useMemo((): Period => {
    const gran = search.granularity ?? "quarter";
    const year = search.periodYear;

    switch (gran) {
      case "year":
        if (year !== undefined) return { kind: "year", year };
        return currentPeriod("year");

      case "quarter": {
        const q = search.periodQuarter as 1 | 2 | 3 | 4 | undefined;
        if (year !== undefined && q !== undefined)
          return { kind: "quarter", year, quarter: q };
        return currentPeriod("quarter");
      }

      case "month": {
        const m = search.periodMonth;
        if (year !== undefined && m !== undefined)
          return { kind: "month", year, month: m };
        return currentPeriod("month");
      }
    }
  }, [
    search.granularity,
    search.periodYear,
    search.periodQuarter,
    search.periodMonth,
  ]);

  const { items, total, isLoading } = useInvoices(filters);
  const hydrate = useHydrateBilling();
  const hydrateCooldownLeftS = Math.ceil(hydrate.cooldownLeftMs / 1000);

  // US-25 UC-25.01 — filtrar la tabla por rango del periodo seleccionado.
  // La tabla ya filtraba por customer; añadimos filtro por fecha de emisión
  // usando `periodRange(period)`. Los items sin `invoice_date` se incluyen para
  // no perder facturas con datos incompletos en el índice local.
  // Nota: `useInvoices(filters)` no filtra por fecha en BD (el filtro quarters
  // legacy fue eliminado en US-25 Fase 3). El índice local es pequeño (~12
  // meses con PAGE_SIZE ≥ 200) y el acotado temporal se hace client-side aquí.
  const filteredItems = useMemo(() => {
    const { from, to } = periodRange(period);
    const fromMs = new Date(from).getTime();
    const toMs = new Date(to + "T23:59:59").getTime();

    return items.filter((it) => {
      // Filtro por periodo usando invoice_date.
      // Incluir si no hay fecha o si cae dentro del rango [from, to].
      if (it.invoice_date) {
        const invoiceMs = new Date(it.invoice_date).getTime();
        if (invoiceMs < fromMs || invoiceMs > toMs) return false;
      }
      // Filtro por cliente (pre-existente).
      if (filters.customer) {
        const needle = filters.customer.toLowerCase();
        if (!it.customer_name.toLowerCase().includes(needle)) return false;
      }
      return true;
    });
  }, [items, filters.customer, period]);

  const { data: selectedInvoice } = useInvoiceDetail(search.selectedId);

  const handleFiltersChange = useCallback(
    (next: FilterValues) => {
      navigate({
        search: (prev: BillingSearch) => ({
          ...prev,
          status: next.status,
          customer: next.customer,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  // US-25 UC-25.01 — AC-01/AC-02: escribe periodo en URL preservando el resto.
  const handlePeriodChange = useCallback(
    (p: Period) => {
      navigate({
        search: (prev: BillingSearch) => ({
          // AC-02: spread de prev preserva status / customer / selectedId.
          ...prev,
          granularity: p.kind,
          periodYear: p.year,
          periodQuarter: p.kind === "quarter" ? p.quarter : undefined,
          periodMonth: p.kind === "month" ? p.month : undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleRowClick = useCallback(
    (invoice: Invoice) => {
      navigate({
        search: (prev: BillingSearch) => ({ ...prev, selectedId: invoice.id }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleCloseDetail = useCallback(() => {
    navigate({
      search: (prev: BillingSearch) => ({ ...prev, selectedId: undefined }),
      replace: true,
    });
  }, [navigate]);

  // UC-20.02 — KPIs sumando lo que ve la tabla (rectificativas ya negativas en BD).
  const kpis = useMemo(() => {
    let total = 0;
    let ivaRepercutido = 0;
    let irpfCuenta = 0;
    for (const inv of filteredItems) {
      // UC-158: la original rectificada suma en positivo y su rectificativa en
      // negativo. No se excluye ninguna: el par netea cero por sí solo.
      total += inv.sum_total ?? 0;
      ivaRepercutido += inv.iva_amount ?? 0;
      irpfCuenta += inv.irpf_amount ?? 0;
    }
    // IVA soportado vendrá cuando exista módulo de gastos; v1 = 0.
    const ivaCuota = ivaRepercutido;
    return { total, ivaRepercutido, irpfCuenta, ivaCuota };
  }, [filteredItems]);

  // US-25 UC-25.01 — etiqueta del periodo activo (p.ej. "Q2 2026", "Mayo 2026").
  const activePeriodLabel = useMemo(() => periodLabel(period), [period]);

  const hasActiveFilters = Boolean(filters.status?.length || filters.customer);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [viewName, setViewName] = useState("");
  // UC-159 — composición de una factura nueva. Fuera de la URL a propósito: un
  // enlace compartido no debe reabrir un formulario de emisión a medio llenar.
  const [showNewInvoice, setShowNewInvoice] = useState(false);

  function handleSaveView() {
    if (!viewName.trim()) return;
    const filterRecord: Record<string, string> = {};
    if (filters.status?.length) filterRecord.status = filters.status.join(",");
    if (filters.customer) filterRecord.customer = filters.customer;
    addSavedView({ name: viewName.trim(), route: "/billing", filters: filterRecord });
    setShowSaveDialog(false);
    setViewName("");
  }

  return (
    <div className="flex flex-col gap-6" data-testid="billing-page">
      {/* Page header */}
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-fg">Facturación</h1>
          <p className="text-sm text-fg-secondary mt-1">
            <span className="tabular font-mono">{total}</span> facturas ·{" "}
            <Amount value={kpis.total} /> facturado
          </p>
        </div>
        {/* US-25 UC-25.01 — Selector de rango temporal */}
        <TimeRangeSelector
          period={period}
          onPeriodChange={handlePeriodChange}
          className="self-end"
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowNewInvoice(true)}
            title="Compone una factura y la emite en FacturaScripts"
            data-testid="billing-new-invoice-button"
            className="inline-flex items-center gap-2 h-8 px-3 rounded-md text-sm font-medium bg-accent text-white hover:bg-accent-strong transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          >
            <FilePlus2 className="size-4" strokeWidth={1.5} aria-hidden />
            Nueva factura
          </button>
          <button
            type="button"
            onClick={() => hydrate.mutate()}
            disabled={hydrate.isPending || hydrateCooldownLeftS > 0}
            title={
              hydrateCooldownLeftS > 0
                ? `Espera ${hydrateCooldownLeftS}s antes del próximo intento`
                : "Hidrata el caché local con las facturas de los últimos 12 meses"
            }
            data-testid="billing-sync-button"
            className="inline-flex items-center gap-2 h-8 px-3 rounded-md text-sm font-medium border border-default text-fg hover:bg-subtle transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw
              className={`size-4 ${hydrate.isPending ? "animate-spin" : ""}`}
              strokeWidth={1.5}
              aria-hidden
            />
            {hydrate.isPending
              ? "Sincronizando…"
              : hydrateCooldownLeftS > 0
                ? `Sincronizar (${hydrateCooldownLeftS}s)`
                : "Sincronizar"}
          </button>
        </div>
      </header>

      {/* KPI Strip — 4 cards limpias sobre los datos visibles en la tabla */}
      <section className="grid grid-cols-12 gap-3" aria-label="KPIs facturación">
        <div className="col-span-3">
          <KPIStat label={`Total · ${activePeriodLabel}`} value={<Amount value={kpis.total} />} />
        </div>
        <div className="col-span-3">
          <KPIStat label="IVA repercutido" value={<Amount value={kpis.ivaRepercutido} />} />
        </div>
        <div className="col-span-3">
          <KPIStat label="IRPF a cuenta" value={<Amount value={kpis.irpfCuenta} />} />
        </div>
        <div className="col-span-3">
          <KPIStat label="IVA cuota" value={<Amount value={kpis.ivaCuota} />} />
        </div>
      </section>

      {/* Evolution Chart — superficie visual principal (UC-25.02) */}
      <Card padding="md">
        <CardHeader className="px-0 pt-0 pb-2">
          <CardTitle>Evolución facturación</CardTitle>
        </CardHeader>
        <BillingEvolutionChart period={period} />
      </Card>

      {/* Fiscal Position — US-25 UC-25.03: mío vs Hacienda */}
      <FiscalPositionCard period={period} />

      {/* UC-162 — composición interna del trimestre. Se pinta solo si la
          granularidad es trimestral; recibe las facturas YA filtradas para que
          el desglose respete cliente y estado como el resto de la pantalla. */}
      <QuarterMonthsBreakdown period={period} invoices={filteredItems} />

      {/* UC-164 — de quién depende la facturación del periodo. Recibe las
          mismas facturas filtradas que la tabla y el desglose. */}
      <PortfolioConcentration
        invoices={filteredItems}
        periodLabel={activePeriodLabel}
      />

      {/* UC-166 — cómo acabaría el ejercicio y qué pasa si facturas de más
          antes del 31 de diciembre. Va siempre sobre el año, no sobre el
          periodo visible: el IRPF se liquida por ejercicio. */}
      <YearEndProjectionCard period={period} />

      {/* Filter bar + save view */}
      <div className="flex items-center justify-between gap-3">
        <BillingFilterBar filters={filters} onFiltersChange={handleFiltersChange} />
        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => setShowSaveDialog(true)}
            className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline shrink-0"
          >
            <Star className="size-4" strokeWidth={1.5} aria-hidden />
            Guardar esta vista
          </button>
        )}
      </div>

      {showSaveDialog && (
        <Card padding="md" className="flex items-center gap-2">
          <input
            type="text"
            autoFocus
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveView();
              if (e.key === "Escape") setShowSaveDialog(false);
            }}
            placeholder="Nombre de la vista (p.ej. Acme pendientes)"
            className="flex-1 h-8 px-3 rounded-md border border-default bg-surface text-sm text-fg placeholder:text-fg-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          />
          <button
            type="button"
            onClick={handleSaveView}
            className="inline-flex items-center justify-center h-8 px-3 rounded-md text-sm font-medium bg-accent text-white hover:bg-accent-strong transition-colors"
          >
            Guardar
          </button>
          <button
            type="button"
            onClick={() => setShowSaveDialog(false)}
            className="inline-flex items-center justify-center h-8 px-3 rounded-md text-sm font-medium border border-default text-fg hover:bg-subtle transition-colors"
          >
            Cancelar
          </button>
        </Card>
      )}

      {/* Table */}
      <Card padding="none" className="overflow-hidden">
        <CardHeader className="px-5 pt-5 pb-3">
          <CardTitle>Facturas</CardTitle>
          <span className="text-xs text-fg-secondary">
            Click en una fila para ver detalle
          </span>
        </CardHeader>
        <BillingTable
          invoices={filteredItems}
          isLoading={isLoading}
          selectedId={search.selectedId ?? null}
          onRowClick={handleRowClick}
        />
      </Card>

      <InvoiceDetailPanel
        invoice={selectedInvoice ?? null}
        open={!!search.selectedId}
        onClose={handleCloseDetail}
      />

      <NewInvoicePanel
        open={showNewInvoice}
        onClose={() => setShowNewInvoice(false)}
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/billing")({
  component: BillingPage,
  validateSearch: billingSearchSchema,
});
